import { FC, useState, useRef, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { FormDto } from 'app/models/generated'
import { Text, Calendar, InputSearch } from '@tuhi/components'
import { FilterChip } from '@tuhi/components/src/redesign/tags/filterTag'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import FilterIcon from '@tuhi/components/assets/icons/filter/filter.svg'
import { media } from '@tuhi/components/src/theme/breakpoints'

const fadeSlideIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px 40px;

    ${media.mobile} {
        padding: 16px;
    }
`

const FiltersContainer = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;

    ${media.mobile} {
        flex-wrap: wrap;
    }
`

const FilterLabel = styled(Text)`
    display: flex;
    align-items: center;
    gap: 4px;
    color: ${(p) => p.theme.colors.tertiary.tertiary900};
`

const Table = styled.table`
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;

    ${media.mobile} {
        display: none;
    }
`

const TableHeader = styled.thead`
    background: ${(p) => p.theme.colors.tertiary.tertiary50};
`

const TableHeaderCell = styled.th<{ $sortable?: boolean }>`
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    font-size: 14px;
    color: ${(p) => p.theme.colors.tertiary.tertiary900};
    cursor: ${(p) => (p.$sortable ? 'pointer' : 'default')};
    user-select: none;

    &:first-child {
        border-top-left-radius: 8px;
    }

    &:last-child {
        border-top-right-radius: 8px;
    }

    &:hover {
        background: ${(p) =>
            p.$sortable ? p.theme.colors.tertiary.tertiary100 : 'inherit'};
    }
`

const TableBody = styled.tbody``

const TableRow = styled.tr<{ $isOdd: boolean; $delay: number }>`
    border-bottom: 1px solid ${(p) => p.theme.colors.tertiary.tertiary100};
    cursor: pointer;
    background-color: ${(p) =>
        p.$isOdd ? p.theme.colors.tertiary.tertiary50 : p.theme.colors.white};
    animation: ${fadeSlideIn} 0.3s ease-out both;
    animation-delay: ${(p) => p.$delay}ms;

    &:hover {
        background: ${(p) => p.theme.colors.primary.green100};
    }
`

const TableCell = styled.td`
    padding: 16px;
    font-size: 14px;
    color: ${(p) => p.theme.colors.tertiary.tertiary900};
`

const StatusBadge = styled.span<{ $status: string }>`
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    background: ${(p) => {
        switch (p.$status.toLowerCase()) {
            case 'pending':
                return '#FFF4E6'
            case 'draft':
                return '#F3F4F6'
            default:
                return '#E8F5E9'
        }
    }};
    color: ${(p) => {
        switch (p.$status.toLowerCase()) {
            case 'pending':
                return '#F59E0B'
            case 'draft':
                return '#6B7280'
            default:
                return '#10B981'
        }
    }};
`

const ActionsCell = styled(TableCell)`
    text-align: center;
`

const DownloadButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    color: ${(p) => p.theme.colors.primary.default};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;

    &:hover {
        background: ${(p) => p.theme.colors.primary.green100};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    svg {
        width: 20px;
        height: 20px;
    }
`

const StateFilterMenu = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 8px;
    background: white;
    border: 1px solid ${(p) => p.theme.colors.tertiary.tertiary200};
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 10;
    min-width: 200px;
    padding: 8px;
`

const StateOption = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    cursor: pointer;
    border-radius: 4px;

    &:hover {
        background: ${(p) => p.theme.colors.tertiary.tertiary50};
    }
`

const StateCheckbox = styled.input`
    cursor: pointer;
`

const StateFilterWrapper = styled.div`
    position: relative;
`

const MobileCardList = styled.div`
    display: none;

    ${media.mobile} {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
`

const MobileCard = styled.div<{ $isOdd: boolean; $delay: number }>`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    border-radius: 8px;
    background-color: ${(p) =>
        p.$isOdd ? p.theme.colors.tertiary.tertiary50 : p.theme.colors.white};
    border: 1px solid ${(p) => p.theme.colors.tertiary.tertiary100};
    cursor: pointer;
    min-height: 44px;
    animation: ${fadeSlideIn} 0.3s ease-out both;
    animation-delay: ${(p) => p.$delay}ms;

    &:active {
        background: ${(p) => p.theme.colors.primary.green100};
    }
`

const MobileCardRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
`

const MobileCardField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`

interface Props {
    forms: FormDto[]
    onFormClick: (formId: string) => void
    onDownloadPDF: (formId: string) => void
    /** ACA-16: when set, completed forms get a "Send to ACC" action. */
    onSendToAcc?: (form: FormDto) => void
}

/** Only a completed form can be sent to ACC (it is the final PDF). */
export const canSendFormToAcc = (form: Pick<FormDto, 'status'>): boolean =>
    (form.status ?? '').toLowerCase() === 'complete'

const SendToAccButton = styled.button`
    background: none;
    border: 1px solid ${(p) => p.theme.colors.primary.default};
    border-radius: 4px;
    color: ${(p) => p.theme.colors.primary.default};
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    padding: 4px 10px;
    margin-right: 4px;
    white-space: nowrap;

    &:hover {
        background: ${(p) => p.theme.colors.primary.green100};
    }
`

export const FormsList: FC<Props> = ({
    forms,
    onFormClick,
    onDownloadPDF,
    onSendToAcc,
}) => {
    const [searchQuery, setSearchQuery] = useState('')
    const [sortField, setSortField] = useState<'sentDate' | null>('sentDate')
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
    const [dateFilters, setDateFilters] = useState<Date[]>([])
    const [dateTitle, setDateTitle] = useState('Date')
    const [stateFilters, setStateFilters] = useState<string[]>([])
    const [stateTitle, setStateTitle] = useState('State')
    const [showStateMenu, setShowStateMenu] = useState(false)
    const [typeFilters, setTypeFilters] = useState<string[]>([])
    const [typeTitle, setTypeTitle] = useState('Type')
    const [showTypeMenu, setShowTypeMenu] = useState(false)

    const calendarRef = useRef<DatePicker>(null)
    const stateMenuRef = useRef<HTMLDivElement>(null)
    const typeMenuRef = useRef<HTMLDivElement>(null)

    // Get unique states and types from forms
    const availableStates = Array.from(
        new Set(forms.map((form) => form.status).filter(Boolean))
    )
    const availableTypes = Array.from(
        new Set(forms.map((form) => form.type).filter(Boolean))
    )

    // Close state menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                stateMenuRef.current &&
                !stateMenuRef.current.contains(event.target as Node)
            ) {
                setShowStateMenu(false)
            }
        }

        if (showStateMenu) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showStateMenu])

    // Close type menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                typeMenuRef.current &&
                !typeMenuRef.current.contains(event.target as Node)
            ) {
                setShowTypeMenu(false)
            }
        }

        if (showTypeMenu) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showTypeMenu])

    const showCalendar = () => {
        calendarRef.current?.setOpen(true)
    }

    const dismissDateFilter = () => {
        setDateFilters([])
    }

    const dismissStateFilter = () => {
        setStateFilters([])
    }

    const dismissTypeFilter = () => {
        setTypeFilters([])
    }

    const toggleStateFilter = (state: string) => {
        setStateFilters((prev) =>
            prev.includes(state)
                ? prev.filter((s) => s !== state)
                : [...prev, state]
        )
    }

    const toggleTypeFilter = (type: string) => {
        setTypeFilters((prev) =>
            prev.includes(type)
                ? prev.filter((t) => t !== type)
                : [...prev, type]
        )
    }

    useEffect(() => {
        if (dateFilters.length === 0) {
            setDateTitle('Date')
        } else {
            setDateTitle(`Date (${dateFilters.length})`)
        }
    }, [dateFilters])

    useEffect(() => {
        if (stateFilters.length === 0) {
            setStateTitle('State')
        } else {
            setStateTitle(`State (${stateFilters.length})`)
        }
    }, [stateFilters])

    useEffect(() => {
        if (typeFilters.length === 0) {
            setTypeTitle('Type')
        } else {
            setTypeTitle(`Type (${typeFilters.length})`)
        }
    }, [typeFilters])

    const handleSort = (field: 'sentDate') => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setSortField(field)
            setSortDirection('desc')
        }
    }

    const filteredForms = forms.filter((form) => {
        const searchLower = searchQuery.toLowerCase()
        const matchesSearch =
            form.title?.toLowerCase().includes(searchLower) ||
            form.type?.toLowerCase().includes(searchLower) ||
            form.status?.toLowerCase().includes(searchLower) ||
            form.patientId?.toLowerCase().includes(searchLower) ||
            form.patientName?.toLowerCase().includes(searchLower)

        // Apply state filter if states are selected
        const matchesState =
            stateFilters.length === 0 || stateFilters.includes(form.status)

        // Apply type filter if types are selected
        const matchesType =
            typeFilters.length === 0 || typeFilters.includes(form.type)

        // Apply date filter if dates are selected
        if (dateFilters.length > 0) {
            const formDate = new Date(form.sentDate)
            const matchesDate = dateFilters.some(
                (date) =>
                    formDate.getDate() === date.getDate() &&
                    formDate.getMonth() === date.getMonth() &&
                    formDate.getFullYear() === date.getFullYear()
            )
            return matchesSearch && matchesState && matchesType && matchesDate
        }

        return matchesSearch && matchesState && matchesType
    })

    const sortedForms = [...filteredForms].sort((a, b) => {
        if (!sortField) return 0

        const aValue = new Date(a[sortField]).getTime()
        const bValue = new Date(b[sortField]).getTime()

        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
    })

    const handleRowClick = (formId: string, e: React.MouseEvent) => {
        // Don't trigger row click if clicking on actions button
        if ((e.target as HTMLElement).closest('button')) {
            return
        }
        onFormClick(formId)
    }

    return (
        <Container>
            <InputSearch
                placeholder="Search forms"
                onChange={(text) => setSearchQuery(text)}
                onSearch={() => {}}
                value={searchQuery}
            />

            <FiltersContainer>
                <FilterLabel size="xxSmall" $weight="medium">
                    <FilterIcon />
                    Filter by:
                </FilterLabel>
                <FilterChip
                    onClick={showCalendar}
                    isSelected={dateFilters.length > 0}
                    text={dateTitle}
                    onDismiss={dismissDateFilter}
                />
                <StateFilterWrapper ref={stateMenuRef}>
                    <FilterChip
                        onClick={() => setShowStateMenu(!showStateMenu)}
                        isSelected={stateFilters.length > 0}
                        text={stateTitle}
                        onDismiss={dismissStateFilter}
                    />
                    {showStateMenu && (
                        <StateFilterMenu>
                            {availableStates.map((state) => (
                                <StateOption key={state}>
                                    <StateCheckbox
                                        type="checkbox"
                                        checked={stateFilters.includes(state)}
                                        onChange={() =>
                                            toggleStateFilter(state)
                                        }
                                    />
                                    <Text size="small" $weight="regular">
                                        {state}
                                    </Text>
                                </StateOption>
                            ))}
                        </StateFilterMenu>
                    )}
                </StateFilterWrapper>
                <StateFilterWrapper ref={typeMenuRef}>
                    <FilterChip
                        onClick={() => setShowTypeMenu(!showTypeMenu)}
                        isSelected={typeFilters.length > 0}
                        text={typeTitle}
                        onDismiss={dismissTypeFilter}
                    />
                    {showTypeMenu && (
                        <StateFilterMenu>
                            {availableTypes.map((type) => (
                                <StateOption key={type}>
                                    <StateCheckbox
                                        type="checkbox"
                                        checked={typeFilters.includes(type)}
                                        onChange={() => toggleTypeFilter(type)}
                                    />
                                    <Text size="small" $weight="regular">
                                        {type}
                                    </Text>
                                </StateOption>
                            ))}
                        </StateFilterMenu>
                    )}
                </StateFilterWrapper>
            </FiltersContainer>

            <Calendar
                ref={calendarRef}
                selectedDates={dateFilters}
                onCalendarClose={(dates) => setDateFilters(dates)}
            />

            <Table>
                <TableHeader>
                    <tr>
                        <TableHeaderCell>NHI</TableHeaderCell>
                        <TableHeaderCell>Patient name</TableHeaderCell>
                        <TableHeaderCell>Form</TableHeaderCell>
                        <TableHeaderCell
                            $sortable
                            onClick={() => handleSort('sentDate')}
                        >
                            State{' '}
                            {sortField === 'sentDate' &&
                                (sortDirection === 'asc' ? '↑' : '↓')}
                        </TableHeaderCell>
                        <TableHeaderCell
                            $sortable
                            onClick={() => handleSort('sentDate')}
                        >
                            Sent date{' '}
                            {sortField === 'sentDate' &&
                                (sortDirection === 'asc' ? '↑' : '↓')}
                        </TableHeaderCell>
                        <TableHeaderCell></TableHeaderCell>
                    </tr>
                </TableHeader>
                <TableBody>
                    {sortedForms.map((form, index) => (
                        <TableRow
                            key={form.id}
                            $isOdd={index % 2 !== 0}
                            $delay={index * 30}
                            onClick={(e) => handleRowClick(form.id, e)}
                        >
                            <TableCell>
                                <Text size="small" $weight="regular">
                                    {form.patientId || '-'}
                                </Text>
                            </TableCell>
                            <TableCell>
                                <Text size="small" $weight="regular">
                                    {form.patientName || 'Unknown'}
                                </Text>
                            </TableCell>
                            <TableCell>
                                <Text size="small" $weight="medium">
                                    {form.type}
                                </Text>
                            </TableCell>
                            <TableCell>
                                <StatusBadge $status={form.status}>
                                    {form.status}
                                </StatusBadge>
                            </TableCell>
                            <TableCell>
                                <Text size="small" $weight="regular">
                                    {new Date(form.sentDate).toLocaleDateString(
                                        'en-GB',
                                        {
                                            weekday: 'short',
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                        }
                                    )}{' '}
                                    {new Date(form.sentDate).toLocaleTimeString(
                                        'en-US',
                                        {
                                            hour: 'numeric',
                                            minute: '2-digit',
                                            hour12: true,
                                        }
                                    )}
                                </Text>
                            </TableCell>
                            <ActionsCell>
                                {onSendToAcc && canSendFormToAcc(form) && (
                                    <SendToAccButton
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            onSendToAcc(form)
                                        }}
                                    >
                                        Send to ACC
                                    </SendToAccButton>
                                )}
                                <DownloadButton
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        onDownloadPDF(form.id)
                                    }}
                                    title="Download PDF"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={2}
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                                        />
                                    </svg>
                                </DownloadButton>
                            </ActionsCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <MobileCardList>
                {sortedForms.map((form, index) => (
                    <MobileCard
                        key={form.id}
                        $isOdd={index % 2 !== 0}
                        $delay={index * 30}
                        onClick={(e) => handleRowClick(form.id, e)}
                    >
                        <MobileCardRow>
                            <MobileCardField>
                                <Text size="small" $weight="medium">
                                    {form.patientName || 'Unknown'}
                                </Text>
                                <Text size="xxSmall" $weight="regular">
                                    {form.patientId || '-'}
                                </Text>
                            </MobileCardField>
                            <StatusBadge $status={form.status}>
                                {form.status}
                            </StatusBadge>
                        </MobileCardRow>
                        <Text size="xSmall" $weight="medium">
                            {form.type}
                        </Text>
                        <MobileCardRow>
                            <Text size="xxSmall" $weight="regular">
                                {new Date(form.sentDate).toLocaleDateString(
                                    'en-GB',
                                    {
                                        weekday: 'short',
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                    }
                                )}{' '}
                                {new Date(form.sentDate).toLocaleTimeString(
                                    'en-US',
                                    {
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true,
                                    }
                                )}
                            </Text>
                            <div>
                                {onSendToAcc && canSendFormToAcc(form) && (
                                    <SendToAccButton
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            onSendToAcc(form)
                                        }}
                                    >
                                        Send to ACC
                                    </SendToAccButton>
                                )}
                                <DownloadButton
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        onDownloadPDF(form.id)
                                    }}
                                    title="Download PDF"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={2}
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                                        />
                                    </svg>
                                </DownloadButton>
                            </div>
                        </MobileCardRow>
                    </MobileCard>
                ))}
            </MobileCardList>
        </Container>
    )
}
